import type { ChartConfig, ChartDataModel, Entitlements, Tier } from '@chartforge/shared-types';
import { isFeatureCapability } from '@chartforge/shared-types';

/**
 * Charts by Nex Flow Automation Sheets bridge protocol.
 *
 * Everything that crosses the Apps Script / iframe boundary is one of the
 * messages declared here. There are deliberately no untyped channels: the host
 * never forwards a raw object to the engine, and the engine never reaches back
 * into Google Sheets. Both directions are:
 *
 *   serialize | postMessage or google.script.run | parseMessage | handler
 *
 * `parseMessage` is the single choke point, so validation covers the untrusted
 * `window.message` path *and* the Apps Script `google.script.run` path with the
 * same code. Its rules:
 *
 * 1. the value must be a plain JSON object (not a function, DOM node, class
 *    instance, array or null-prototype object),
 * 2. `version`, `tag` and `kind` must match this build exactly,
 * 3. `channel` must equal the token minted per sidebar session (an iframe
 *    cannot guess it, which is what makes forgery detectable),
 * 4. the payload must satisfy every field's type and range for that kind,
 *    recursively, with no unknown fields accepted,
 * 5. the encoded envelope must stay below `MAX_MESSAGE_BYTES`.
 *
 * Anything else is rejected as a ProtocolError. Unknown messages are never
 * silently ignored: they are surfaced with a stable reason code so a broken
 * bridge is diagnosable instead of mysterious.
 */

/** Must match on both sides; a mismatch disables the bridge with a clear error. */
export const PROTOCOL_VERSION = 1;

/** Envelope brand, so unrelated `window.message` traffic is ignored instantly. */
export const BRIDGE_TAG = 'chartforge.sheets.bridge';

/** Message directions. Each side only accepts its own half of the protocol. */
export const HOST_TO_CLIENT_KINDS = [
  'host:init',
  'host:model',
  'host:entitlement',
  'host:config',
  'host:error'
] as const;

export const CLIENT_TO_HOST_KINDS = [
  'client:ready',
  'client:config:change',
  'client:refresh',
  'client:export',
  'client:error'
] as const;

export type HostToClientKind = (typeof HOST_TO_CLIENT_KINDS)[number];
export type ClientToHostKind = (typeof CLIENT_TO_HOST_KINDS)[number];
export type MessageKind = HostToClientKind | ClientToHostKind;

/* ------------------------------------------------------------------ */
/* Payloads                                                            */
/* ------------------------------------------------------------------ */

/** Where the model came from, for diagnostics and for the "re-read" action. */
export interface SelectionReference {
  sheetName: string;
  a1Notation: string;
  numRows: number;
  numColumns: number;
  /** True when the selection is a single cell: the host asks the user to extend it. */
  singleCell: boolean;
}

export interface InitTransportInfo {
  /** `iframe` = engine served over HTTPS and embedded; `sandbox` = google.script.run. */
  transport: 'iframe' | 'sandbox' | 'direct';
  /** Engine URL for the iframe transport, otherwise null. */
  engineUrl: string | null;
}

/**
 * Where the current entitlements came from. The verified entitlement JWT
 * itself never leaves the Apps Script host: the client is told the plan, its
 * capabilities and the provenance — never the token.
 *
 * `grace` (M4) means an expired token is still inside the configured offline
 * window, so the sidebar keeps the last verified plan and can say so.
 */
export type EntitlementSource =
  'cache-hit' | 'verified' | 'cache-miss' | 'grace' | 'fallback' | 'stub';

export interface HostInitPayload {
  /** Protocol-level session id, echoed back on read-only client messages. */
  sessionId: string;
  selection: SelectionReference | null;
  /** `timeline` today; resolved through the shared ChartRegistry, never ad hoc. */
  chartId: string | null;
  config: ChartConfig | null;
  entitlements: Entitlements;
  entitlementSource: EntitlementSource;
  /** Add-on version string (informational; shown in the sidebar footer). */
  addonVersion: string;
  transport: InitTransportInfo;
}

export interface ModelMessagePayload {
  model: ChartDataModel;
  selection: SelectionReference | null;
}

export interface ConfigMessagePayload {
  config: ChartConfig;
}

export interface EntitlementMessagePayload {
  entitlements: Entitlements;
  source: EntitlementSource;
}

export interface ErrorMessagePayload {
  /** Stable machine-readable code, e.g. `SHEET_RANGE_EMPTY`. */
  code: string;
  message: string;
  /** Field path for protocol rejections (`payload.model.series[2].points[0].value`). */
  path?: string;
  /** True when the host believes a retry after user action could succeed. */
  recoverable?: boolean;
}

export interface ReadyPayload {
  /** Client build id, so the host log shows which bundle a user actually ran. */
  clientVersion: string;
  protocolVersion: number;
}

export interface ConfigChangePayload {
  config: ChartConfig;
  /** Option ids whose values changed in this edit (host-side audit/telemetry). */
  changedOptionIds: string[];
}

export interface RefreshPayload {
  /** True when the user explicitly asked for a re-read (button click). */
  explicit: boolean;
}

export interface ExportPayload {
  format: 'svg' | 'png';
  /** Serialized artifact, or null when the client is probing support first. */
  data: string | null;
}

/* ------------------------------------------------------------------ */
/* Envelope                                                            */
/* ------------------------------------------------------------------ */

export interface MessageEnvelope<K extends MessageKind = MessageKind> {
  /** Envelope brand: see BRIDGE_TAG. */
  tag: typeof BRIDGE_TAG;
  version: number;
  kind: K;
  /** Per-session secret shared out-of-band with the injected client config. */
  channel: string;
  /** Present on request/response pairs; absent on unsolicited pushes. */
  requestId?: string;
  /** Sender timestamp (epoch ms); informational only, never trusted for logic. */
  sentAt: number;
  payload: PayloadFor<K>;
}

export type PayloadFor<K extends MessageKind> = K extends 'host:init'
  ? HostInitPayload
  : K extends 'host:model'
    ? ModelMessagePayload
    : K extends 'host:entitlement'
      ? EntitlementMessagePayload
      : K extends 'host:config'
        ? ConfigMessagePayload
        : K extends 'host:error'
          ? ErrorMessagePayload
          : K extends 'client:ready'
            ? ReadyPayload
            : K extends 'client:config:change'
              ? ConfigChangePayload
              : K extends 'client:refresh'
                ? RefreshPayload
                : K extends 'client:export'
                  ? ExportPayload
                  : K extends 'client:error'
                    ? ErrorMessagePayload
                    : never;

/**
 * Every message the bridge can carry, discriminated by `kind`. Both endpoints
 * switch on this union, which is why an unknown `kind` is a compile error
 * rather than a runtime surprise.
 */
export type BridgeMessage = { [K in MessageKind]: MessageEnvelope<K> }[MessageKind];

/* ------------------------------------------------------------------ */
/* Limits                                                              */
/* ------------------------------------------------------------------ */

/** Hard limits. A sidebar payload is a few kB; these bound hostile input. */
export const MAX_MESSAGE_BYTES = 256 * 1024;
export const MAX_CATEGORIES = 5_000;
export const MAX_SERIES = 64;
export const MAX_POINTS = 20_000;
export const MAX_STRING_LENGTH = 512;
export const MAX_OPTION_KEYS = 256;

/* ------------------------------------------------------------------ */
/* Validation                                                          */
/* ------------------------------------------------------------------ */

export type ProtocolErrorCode =
  | 'NOT_AN_OBJECT'
  | 'BAD_TAG'
  | 'BAD_VERSION'
  | 'UNKNOWN_KIND'
  | 'DIRECTION_MISMATCH'
  | 'BAD_CHANNEL'
  | 'BAD_PAYLOAD'
  | 'TOO_LARGE';

/** Thrown by `parseMessage` for anything that is not a valid message. */
export class ProtocolError extends Error {
  constructor(
    readonly code: ProtocolErrorCode,
    message: string,
    readonly path?: string
  ) {
    super(message);
    this.name = 'ProtocolError';
  }
}

export type Direction = 'host-to-client' | 'client-to-host';

/** Directions a single endpoint accepts. */
export const HOST_ACCEPTS: Direction = 'client-to-host';
export const CLIENT_ACCEPTS: Direction = 'host-to-client';

function kindsFor(direction: Direction): readonly MessageKind[] {
  return direction === 'host-to-client' ? HOST_TO_CLIENT_KINDS : CLIENT_TO_HOST_KINDS;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isBoundedString(value: unknown, max = MAX_STRING_LENGTH): value is string {
  return typeof value === 'string' && value.length <= max;
}

function isTier(value: unknown): value is Tier {
  return value === 'trial' || value === 'basic' || value === 'pro';
}

const LICENSE_STATUSES = [
  'active',
  'trialing',
  'past_due',
  'canceled',
  'paused',
  'expired',
  'none'
] as const;

const ENTITLEMENT_ORIGINS = ['verified', 'cache', 'grace', 'trial', 'fallback', 'stub'] as const;

/**
 * Structural validation of the shared `Entitlements` contract.
 *
 * Every M4 addition is optional AND validated when present, so the bridge keeps
 * its "no unknown fields accepted" property: a plan can arrive alone (the exact
 * M0–M3 payload) or with the M4 fields, but never with a capability list full
 * of values the engine would not understand.
 */
export function isEntitlements(value: unknown): value is Entitlements {
  if (!isPlainObject(value) || !isTier(value.plan)) {
    return false;
  }
  if (value.features !== undefined) {
    if (!Array.isArray(value.features) || value.features.length > 16) {
      return false;
    }
    if (!value.features.every((feature: unknown) => isFeatureCapability(feature))) {
      return false;
    }
  }
  if (
    value.status !== undefined &&
    !(LICENSE_STATUSES as readonly string[]).includes(String(value.status))
  ) {
    return false;
  }
  if (value.expiresAt !== undefined && !isFiniteNumber(value.expiresAt)) {
    return false;
  }
  if (value.graceUntil !== undefined && !isFiniteNumber(value.graceUntil)) {
    return false;
  }
  if (value.deviceBound !== undefined && typeof value.deviceBound !== 'boolean') {
    return false;
  }
  if (value.licenseKeyHash !== undefined && !isShortString(value.licenseKeyHash, 128)) {
    return false;
  }
  if (
    value.origin !== undefined &&
    !(ENTITLEMENT_ORIGINS as readonly string[]).includes(String(value.origin))
  ) {
    return false;
  }
  return true;
}

const ENTITLEMENT_SOURCES: readonly EntitlementSource[] = [
  'cache-hit',
  'verified',
  'cache-miss',
  'grace',
  'fallback',
  'stub'
];

export function isEntitlementSource(value: unknown): value is EntitlementSource {
  return typeof value === 'string' && (ENTITLEMENT_SOURCES as readonly string[]).includes(value);
}

export function isSelectionReference(value: unknown): value is SelectionReference {
  if (!isPlainObject(value)) {
    return false;
  }
  return (
    isBoundedString(value.sheetName) &&
    isBoundedString(value.a1Notation) &&
    isFiniteNumber(value.numRows) &&
    isFiniteNumber(value.numColumns) &&
    typeof value.singleCell === 'boolean'
  );
}

const SERIES_TYPES = ['timeline', 'line', 'bar', 'area', 'scatter'] as const;

function isDatum(value: unknown): boolean {
  if (!isPlainObject(value)) {
    return false;
  }
  if (!isBoundedString(value.category) || !isFiniteNumber(value.value)) {
    return false;
  }
  return value.extra === undefined || isPlainObject(value.extra);
}

function isSeries(value: unknown): boolean {
  if (!isPlainObject(value)) {
    return false;
  }
  if (!isBoundedString(value.id) || !isBoundedString(value.name)) {
    return false;
  }
  if (typeof value.type !== 'string' || !(SERIES_TYPES as readonly string[]).includes(value.type)) {
    return false;
  }
  if (!Array.isArray(value.points) || value.points.length > MAX_POINTS) {
    return false;
  }
  return value.points.every(isDatum);
}

/**
 * Structural check of the *shared* data contract. The engine's plugin
 * validation still runs on the client (`renderChart` then `plugin.validate`),
 * so this only guarantees the model is shaped like a ChartDataModel --
 * semantics stay in exactly one place.
 */
export function isChartDataModel(value: unknown): value is ChartDataModel {
  if (!isPlainObject(value)) {
    return false;
  }
  if (!Array.isArray(value.categories) || value.categories.length > MAX_CATEGORIES) {
    return false;
  }
  if (!value.categories.every((c: unknown) => isBoundedString(c))) {
    return false;
  }
  if (!Array.isArray(value.series) || value.series.length > MAX_SERIES) {
    return false;
  }
  if (!value.series.every(isSeries)) {
    return false;
  }
  if (value.meta !== undefined) {
    if (!isPlainObject(value.meta)) {
      return false;
    }
    if (value.meta.source !== undefined && !isBoundedString(value.meta.source)) {
      return false;
    }
    if (value.meta.units !== undefined && !isBoundedString(value.meta.units)) {
      return false;
    }
  }
  return true;
}

/** Config buckets, mirroring the engine's `CONFIG_GROUPS` contract. */
export const CONFIG_GROUPS = ['appearance', 'layout', 'axes', 'labels', 'data'] as const;

/** A ChartConfig is five flat JSON buckets -- never a nested host object graph. */
export function isChartConfig(value: unknown): value is ChartConfig {
  if (!isPlainObject(value)) {
    return false;
  }
  for (const group of CONFIG_GROUPS) {
    const bucket: unknown = value[group];
    if (!isPlainObject(bucket)) {
      return false;
    }
    const keys = Object.keys(bucket);
    if (keys.length > MAX_OPTION_KEYS) {
      return false;
    }
    for (const key of keys) {
      if (!isJsonValue(bucket[key], 0)) {
        return false;
      }
    }
  }
  return true;
}

function isJsonValue(value: unknown, depth: number): boolean {
  if (depth > 4) {
    return false;
  }
  if (value === null || typeof value === 'boolean') {
    return true;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  if (typeof value === 'string') {
    return value.length <= 4096;
  }
  if (Array.isArray(value)) {
    return value.length <= 64 && value.every((item) => isJsonValue(item, depth + 1));
  }
  if (isPlainObject(value)) {
    const keys = Object.keys(value);
    return keys.length <= 64 && keys.every((key) => isJsonValue(value[key], depth + 1));
  }
  return false;
}

function isShortString(value: unknown, max: number): value is string {
  return isBoundedString(value, max);
}

function isTransportInfo(value: unknown): value is InitTransportInfo {
  if (!isPlainObject(value)) {
    return false;
  }
  const transportOk =
    value.transport === 'iframe' || value.transport === 'sandbox' || value.transport === 'direct';
  if (!transportOk) {
    return false;
  }
  if (value.engineUrl === null) {
    return true;
  }
  return isShortString(value.engineUrl, 2048) && /^https?:\/\//.test(value.engineUrl);
}

function isErrorPayload(value: unknown): boolean {
  if (!isPlainObject(value)) {
    return false;
  }
  if (!isShortString(value.code, 64) || !isShortString(value.message, 2048)) {
    return false;
  }
  if (value.path !== undefined && !isShortString(value.path, 256)) {
    return false;
  }
  if (value.recoverable !== undefined && typeof value.recoverable !== 'boolean') {
    return false;
  }
  return true;
}

/**
 * Per-kind payload validators. Adding a message kind means adding exactly one
 * entry here plus one payload interface above; nothing else in the codebase
 * changes, mirroring how one OptionSchema entry drives the settings UI.
 */
const PAYLOAD_VALIDATORS: Record<MessageKind, (payload: unknown) => boolean> = {
  'host:init': (p) => {
    if (!isPlainObject(p)) {
      return false;
    }
    if (!isShortString(p.sessionId, 64) || !isShortString(p.addonVersion, 64)) {
      return false;
    }
    if (p.selection !== null && !isSelectionReference(p.selection)) {
      return false;
    }
    if (p.chartId !== null && !isShortString(p.chartId, 64)) {
      return false;
    }
    if (p.config !== null && !isChartConfig(p.config)) {
      return false;
    }
    if (!isEntitlements(p.entitlements) || !isEntitlementSource(p.entitlementSource)) {
      return false;
    }
    return isTransportInfo(p.transport);
  },
  'host:model': (p) =>
    isPlainObject(p) &&
    isChartDataModel(p.model) &&
    (p.selection === null || isSelectionReference(p.selection)),
  'host:config': (p) => isPlainObject(p) && isChartConfig(p.config),
  'host:entitlement': (p) =>
    isPlainObject(p) && isEntitlements(p.entitlements) && isEntitlementSource(p.source),
  'host:error': isErrorPayload,
  'client:ready': (p) =>
    isPlainObject(p) &&
    isShortString(p.clientVersion, 64) &&
    isFiniteNumber(p.protocolVersion) &&
    p.protocolVersion === PROTOCOL_VERSION,
  'client:config:change': (p) =>
    isPlainObject(p) &&
    isChartConfig(p.config) &&
    Array.isArray(p.changedOptionIds) &&
    p.changedOptionIds.length <= MAX_OPTION_KEYS &&
    p.changedOptionIds.every((id: unknown) => isShortString(id, 128)),
  'client:refresh': (p) => isPlainObject(p) && typeof p.explicit === 'boolean',
  'client:export': (p) =>
    isPlainObject(p) &&
    (p.format === 'svg' || p.format === 'png') &&
    (p.data === null || isShortString(p.data, MAX_MESSAGE_BYTES)),
  'client:error': isErrorPayload
};

/** A channel token is minted per sidebar session and required on every message. */
export function isValidChannelToken(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{16,128}$/.test(value);
}

export interface ParseOptions {
  /** Which half of the protocol the caller is allowed to receive. */
  direction: Direction;
  /** Token minted for this session; a mismatch is rejected, not ignored. */
  channel: string;
  /** Overridable for unit tests that pin protocol compatibility. */
  expectedVersion?: number;
  /** Overridable message-size ceiling (bytes of the encoded envelope). */
  maxBytes?: number;
}

/**
 * Validate an inbound value as a bridge message for one direction.
 *
 * @throws ProtocolError for anything malformed. Callers decide whether to log,
 *         answer with `host:error`, or drop the message. Never returns a
 *         partially-validated object.
 */
export function parseMessage(value: unknown, options: ParseOptions): BridgeMessage {
  if (!isPlainObject(value)) {
    throw new ProtocolError('NOT_AN_OBJECT', 'Bridge message must be a plain JSON object');
  }
  if (value.tag !== BRIDGE_TAG) {
    throw new ProtocolError('BAD_TAG', `Unknown bridge tag: ${String(value.tag)}`, 'tag');
  }
  const expectedVersion = options.expectedVersion ?? PROTOCOL_VERSION;
  if (value.version !== expectedVersion) {
    throw new ProtocolError(
      'BAD_VERSION',
      `Bridge protocol version mismatch: expected ${expectedVersion}, got ${String(value.version)}`,
      'version'
    );
  }
  const kind = value.kind;
  if (typeof kind !== 'string' || !isKnownKind(kind)) {
    throw new ProtocolError('UNKNOWN_KIND', `Unknown message kind: ${String(kind)}`, 'kind');
  }
  if (!kindsFor(options.direction).includes(kind)) {
    throw new ProtocolError(
      'DIRECTION_MISMATCH',
      `Message "${kind}" is not accepted from the ${options.direction} side`,
      'kind'
    );
  }
  if (!isValidChannelToken(value.channel) || value.channel !== options.channel) {
    throw new ProtocolError(
      'BAD_CHANNEL',
      'Bridge channel token missing or does not match this session',
      'channel'
    );
  }
  if (!isFiniteNumber(value.sentAt)) {
    throw new ProtocolError(
      'BAD_PAYLOAD',
      'sentAt must be a finite epoch-millisecond number',
      'sentAt'
    );
  }
  if (value.requestId !== undefined && !isShortString(value.requestId, 128)) {
    throw new ProtocolError('BAD_PAYLOAD', 'requestId must be a bounded string', 'requestId');
  }
  const maxBytes = options.maxBytes ?? MAX_MESSAGE_BYTES;
  const size = encodedByteLength(value);
  if (size > maxBytes) {
    throw new ProtocolError(
      'TOO_LARGE',
      `Bridge message is ${size} bytes, above the ${maxBytes}-byte limit`,
      'payload'
    );
  }
  if (!PAYLOAD_VALIDATORS[kind](value.payload)) {
    throw new ProtocolError('BAD_PAYLOAD', `Malformed payload for "${kind}"`, 'payload');
  }
  // Every rule above has been applied, so the envelope is known-good; the
  // assertion only restores the discriminated union TypeScript cannot infer
  // from a runtime-validated value.
  return value as unknown as BridgeMessage;
}

function isKnownKind(value: string): value is MessageKind {
  return (
    (HOST_TO_CLIENT_KINDS as readonly string[]).includes(value) ||
    (CLIENT_TO_HOST_KINDS as readonly string[]).includes(value)
  );
}

function encodedByteLength(value: Record<string, unknown>): number {
  try {
    const encoded = JSON.stringify(value);
    // TextEncoder is not guaranteed in Apps Script's V8 runtime, so fall back
    // to a conservative UTF-16 estimate: the limit holds in both runtimes.
    if (typeof TextEncoder !== 'undefined') {
      return new TextEncoder().encode(encoded).length;
    }
    return encoded.length * 2;
  } catch {
    // Cyclic or otherwise non-serializable input can never be a bridge message.
    return Number.POSITIVE_INFINITY;
  }
}

/* ------------------------------------------------------------------ */
/* Serialization                                                       */
/* ------------------------------------------------------------------ */

export interface CreateMessageOptions {
  channel: string;
  requestId?: string;
  /** Injectable clock: keeps tests deterministic. */
  now?: () => number;
}

/** Build a well-formed envelope. The only sanctioned way to mint messages. */
export function createMessage<K extends MessageKind>(
  kind: K,
  payload: PayloadFor<K>,
  options: CreateMessageOptions
): MessageEnvelope<K> {
  const envelope: MessageEnvelope<K> = {
    tag: BRIDGE_TAG,
    version: PROTOCOL_VERSION,
    kind,
    channel: options.channel,
    sentAt: (options.now ?? Date.now)(),
    payload
  };
  if (options.requestId !== undefined) {
    envelope.requestId = options.requestId;
  }
  return envelope;
}

/** `postMessage` needs a structured-cloneable value; templates need a string. */
export function serializeMessage(message: BridgeMessage): string {
  return JSON.stringify(message);
}

/** Parse text produced by `serializeMessage` (Apps Script template path). */
export function deserializeMessage(text: string, options: ParseOptions): BridgeMessage {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new ProtocolError('NOT_AN_OBJECT', 'Bridge message is not valid JSON');
  }
  return parseMessage(parsed, options);
}

/** Stable error payload for a rejected message, used by both bridges. */
export function protocolErrorPayload(error: unknown): ErrorMessagePayload {
  if (error instanceof ProtocolError) {
    return { code: `PROTOCOL_${error.code}`, message: error.message, path: error.path };
  }
  return {
    code: 'PROTOCOL_UNKNOWN',
    message: error instanceof Error ? error.message : String(error)
  };
}

/**
 * Diff two configs and return the option ids whose values changed. The host
 * uses this to record changes and to reject no-op round trips.
 */
export function changedOptionIds(previous: ChartConfig, next: ChartConfig): string[] {
  const ids: string[] = [];
  for (const group of CONFIG_GROUPS) {
    const before = previous[group] ?? {};
    const after = next[group] ?? {};
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    for (const key of keys) {
      if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
        ids.push(key);
      }
    }
  }
  return ids;
}
